import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthLayout } from '../../components/auth/AuthLayout';
import { OAuthButtons } from '../../components/auth/OAuthButtons';
import { FormError, OrDivider, PasswordField, SubmitButton, TextField } from '../../components/auth/FormFields';
import { authApi, readApiError } from '../../lib/auth-api';

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const { user } = await authApi.login({ email, password });
      // PlyEZ-staff users (role 'admin' with no company) land on /admin.
      // Workspace users go to /app; brand-new users finish onboarding first.
      if (user.companyId) navigate('/app');
      else if (user.role === 'admin') navigate('/admin');
      else navigate('/onboarding');
    } catch (e) {
      setErr(readApiError(e, t('auth.errorInvalidCredentials')));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      eyebrow={t('auth.signInEyebrow')}
      title={t('auth.signInTitle')}
      subtitle={t('auth.signInSubtitle')}
      altCta={{ to: '/signup', helper: t('auth.noAccount'), label: t('auth.signUpLink') }}
    >
      <OAuthButtons context="login" />
      <OrDivider label={t('auth.or')} />
      <form onSubmit={onSubmit} className="space-y-4">
        <TextField
          label={t('common.email')}
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <PasswordField
          label={t('auth.password')}
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {err && <FormError>{err}</FormError>}
        <div className="flex items-center justify-between">
          <Link to="/forgot-password" className="text-sm text-accent-700 hover:underline">
            {t('auth.forgotPassword')}
          </Link>
        </div>
        <SubmitButton loading={busy}>{t('auth.signInButton')}</SubmitButton>
      </form>
    </AuthLayout>
  );
}
